import React, { useState, useEffect, createContext, useContext } from "react";
import { getFleetTypes } from "../services/otsEventService";
import { useAuthentication } from "meportal/AuthenticationProvider";
import { logError } from "meportal/Logger";
import { getDatetimeUtcNowString } from "../helpers/DateTime";
import { APP_NAME_APPINSIGHTS } from "../constants/Constants";

export type FleetTypeContextType = {
  fleetTypes: any[];
};

const FleetTypeContext = createContext<FleetTypeContextType | null>(null);

export const FleetTypeProvider = ({ children }) => {
  const moduleName = `${APP_NAME_APPINSIGHTS}-FleetTypeProvider`;
  const [fleetTypes, setFleetTypes] = useState<any[]>([]);
  const { userData, getLatestAccessToken } = useAuthentication();

  useEffect(() => {
    let isMounted = true;
    async function fetchFleetTypes() {
      try {
        if (userData.access_token !== "") {
          const token = await getLatestAccessToken();
          userData.access_token = token;
          const response = await getFleetTypes(userData);
          if (isMounted && response) {
            setFleetTypes(response);
          }
        }
      } catch (error) {
        logError(
            `${getDatetimeUtcNowString("YYYY-MM-DD HH:mm:ss")}Z => Error occurred while fetching fleet types. User: ${userData?.profile?.email} | ` +
            (error instanceof Error ? `${error.message} | ${error.stack}` : JSON.stringify(error)),
            moduleName,
            "fetchFleetTypes"
          );
      }
    }
    fetchFleetTypes();
    return () => {
      isMounted = false;
    };
  }, [userData]);

  return (
    <FleetTypeContext.Provider value={{ fleetTypes }}>
      {children}
    </FleetTypeContext.Provider>
  );
};

export const useFleetTypes = () => {
  const context = useContext(FleetTypeContext);
  if (!context) {
    throw new Error(
      "useFleetTypes must be used within a FleetTypeProvider. Wrap a parent component in <FleetTypeProvider> to fix this error."
    );
  }
  return context;
};
